import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAppMeta, useTablePagination } from "hooks";
import { Box, Button, IconButton, Chip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListShipmentRefundsQuery } from "store/services/PaymentsApi/shipmentRefundApi";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#FEF9E7", textColor: "#D4AC0D" },
    APPROVED: { backgroundColor: "#EBF5FB", textColor: "green" },
    REJECTED: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };

  return (
    styles[status?.toUpperCase()] || {
      backgroundColor: "#F8F9F9",
      textColor: "#7F8C8D",
    }
  );
};

const ShipmentRefundListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Shipment Refunds" }];

  const refundTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Refund ID",
      selector: (row) => row.refundId,
      cell: (row) => (
        <Link
          title={row?.refundId}
          to={`/app/shipment-refund/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.refundId ? (
            <HighlightedText text={row?.refundId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Shipment",
      selector: (row) => row.shipment?.shipmentId,
      cell: (row) => (
        <Link
          title={row?.shipment?.shipmentId}
          to={`/app/crm/shipment/${row?.shipment?._id}/detail?tab=pricing`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.shipment?.shipmentId ? (
            <HighlightedText
              text={row?.shipment?.shipmentId}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Sender",
      selector: (row) =>
        row?.customerId?.customerUser?.name ||
        row?.vendor?.shopName ||
        row?.vendor?.ownerName ||
        row?.vendor?.vendor?.name ||
        "N/A",
      cell: (row) => (
        <Box sx={{ maxWidth: "600px", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row?.customerId?.customerUser?.name
            ? `CUS: ${row.customerId.customerUser.name}`
            : row?.vendor?.shopName || row?.vendor?.ownerName || row?.vendor?.vendor?.name
              ? `VEN: ${row.vendor.shopName || row.vendor.ownerName || row.vendor.vendor?.name}`
              : "N/A"}
        </Box>
      ),
    },
    {
      name: "Refund Amount",
      selector: (row) => row.amount,
      cell: (row) =>
        row?.amount != null ? (
          <span className="text-sm font-medium">{row.amount}</span>
        ) : (
          "N/A"
        ),
    },
    {
      name: "Refund Type",
      selector: (row) => row.refundType?.name,
      cell: (row) => row?.refundType?.name ?? "N/A",
    },
    {
      name: "Method",
      selector: (row) => row.refundMethod,
      cell: (row) => row?.refundMethod ?? "N/A",
    },
    {
      name: "Refund Date",
      selector: (row) => <AppTableDateTime date={row?.refundDate} />,
    },
    {
      name: "Status",
      selector: (row) => row.refundStatus,
      cell: (row) => {
        const statusName = row?.refundStatus || "N/A";
        const { backgroundColor, textColor } = getStatusStyle(statusName);

        return (
          <Chip
            label={statusName}
            size="small"
            sx={{
              backgroundColor,
              color: textColor,
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/shipment-refund/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Refund" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          {row?.refundStatus !== "Approved" && (
            <Link to={`/app/crm/shipment-refund/${row._id}/edit`}>
              <IconButton size="small" aria-label="Edit Refund">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )}
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchShipmentRefunds,
    { data: shipmentRefundRes, isLoading, isFetching, error },
  ] = useLazyListShipmentRefundsQuery();

  React.useEffect(() => {
    fetchShipmentRefunds({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchShipmentRefunds, keyword, perPage]);

  useAppMeta(`${activeTab} Shipment Refunds`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={refundTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search Shipment Refunds"
          disabled={isLoading || isFetching}
        />

        <AppTable
          columns={columns}
          data={shipmentRefundRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRefundRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ShipmentRefundListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/shipment-refund/create">
      <Button startIcon={<Add />} variant="contained">
        New Shipment Refund
      </Button>
    </Link>
  );
};
